import { tokens } from '@neo4j-ndl/base';
import { Accordion, Banner, Button, LoadingSpinner, TextLink, Tooltip, Typography } from '@neo4j-ndl/react';
import { OPS_EVENTS } from '@nx/analytics-service';
import type { Instance, SeriesSummary, TimePeriod, TimeRange } from '@nx/state';
import {
  ACTION,
  INSTANCE_STATUS,
  METRIC_TYPE,
  TIER,
  consoleApi,
  useActiveProject,
  useInstances,
  useOpsContext,
  usePermissions,
} from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { BackButton, Center, useDarkMode } from '@nx/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { some } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { useModalState } from '../log-forwarding/hooks/use-modal-state';
import { datesWithin24H } from '../logs/shared/utils';
import type { ChartWidgetPartialProps } from '../metrics/charts/fullstack-chart-props';
import { IntChartConfig } from '../metrics/charts/helpers';
import { TimelineCard } from '../metrics/charts/line-cards';
import { QueryExecutionsSuccessRate } from '../metrics/charts/timeline-charts';
import { useMetricsQuery, useMetricsViewState } from '../metrics/hooks';
import { COLOR_PALETTE_ENUM, DARK_MODE_SHADE, LIGHT_MODE_SHADE } from '../metrics/shared/color-palettes';
import { TimePeriodAndRefresh } from '../metrics/shared/components/time-window-selector';
import { analyticsPropsFromTimeRange } from '../metrics/shared/utils';
import { track } from '../services/segment/analytics';
import { metricKey } from '../shared/utils';
import { needsMigration } from './helpers';
import { toDisplayName } from './mappers';
import { deprecationMetricMetadata } from './metadata';
import './migration-assistant-page.css';
import { DeprecatedIndexTypes, DeprecationLogs, DriverLogs } from './migration-sections';
import { CloningInProgressSection } from './migration-sections/test-and-execute/cloning-in-progress-section';
import { kindOfMigration } from './migration-sections/test-and-execute/common';
import { MigrationEvaluationSection } from './migration-sections/test-and-execute/migration-evaluation-section';
import { MigrationModal } from './migration-sections/test-and-execute/migration-modal';

const { allProps: allChartProps, pills: chartPills } = (() => {
  const leftProps: ChartWidgetPartialProps[] = deprecationMetricMetadata.map((m) => ({
    title: m.metricName,
    subtitle: m.metricName,
    yLabel: m.metricName,
    yAxisLabel: 'Deprecation occurrences',
    metricType: METRIC_TYPE.Gauge,
    metricName: m.metricName,
    chartType: 'HOST',
    aggregations: ['MAX'],
    chartPathType: 'LINE',
    colorPalette: COLOR_PALETTE_ENUM.ExcludingCountColor,
    ...IntChartConfig,
  }));
  const pills: SeriesSummary[] = [
    {
      id: 'Count',
      name: 'Queries per minute',
      displayName: 'Queries per minute',
      areaAlpha: 70,
    },
    ...deprecationMetricMetadata.map((m) => ({
      id: m.metricName,
      name: m.metricName,
      displayName: toDisplayName(m.metricName),
    })),
  ];

  return { allProps: [QueryExecutionsSuccessRate, ...leftProps], pills };
})();

const seriesNameMapper = (props: ChartWidgetPartialProps) => props.subtitle ?? '';

export const MigrationAssistantPage = () => {
  const testMigrationModal = useModalState();
  const liveMigrationModal = useModalState();
  const isDarkTheme = useDarkMode();
  const pathParams = useParams<'projectId' | 'instanceId'>();
  const { projectId, instanceId } = pathParams;

  const activeProject = useActiveProject();
  const { isAllowed } = usePermissions();
  const allowDelete = isAllowed(ACTION.DELETE, `namespaces/${activeProject.id}/databases/${instanceId}`);
  const allowClone = isAllowed(ACTION.CREATE, `namespaces/${activeProject.id}/databases`);

  const { setSelectedInstance } = useOpsContext();
  useEffect(() => {
    // Set the instance state in case user navigates to QLA or Metrics
    setSelectedInstance(instanceId);
  }, [instanceId, setSelectedInstance]);

  const { data: instance, isLoading, error } = consoleApi.useGetInstanceQuery(instanceId ?? skipToken);

  const { chartContext, setTimePeriod, zoomIn, refreshTimeRange } = useMetricsViewState({
    timePeriod: '24H',
  });

  const setTimePeriodTracked: (timePeriod: TimePeriod) => void = useCallback(
    (timePeriod: TimePeriod): void => {
      setTimePeriod(timePeriod);
      track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CHANGE_TIME_PERIOD, { timePeriod });
    },
    [setTimePeriod],
  );

  const selectedEntityDetails = useMemo(() => {
    if (instance) {
      return {
        dbmsId: instance.id,
        tenantId: instance.projectId,
        name: instance.name,
      };
    }
    return undefined;
  }, [instance]);

  // TODO shared metric chart handling?
  const metricsResult = useMetricsQuery({
    chartProps: allChartProps,
    chartType: 'DB',
    timeRange: chartContext.timeRange,
    selectedEntityDetails,
    useAggregations: false,
    seriesNameMapper,
    database: '*',
  });

  const hasQueries = useMemo(() => {
    const dataPointsGreaterThan0 = (chartProps: ChartWidgetPartialProps) =>
      metricsResult.metrics &&
      some(metricsResult.metrics[metricKey(chartProps, instanceId)]?.dataSeries, (metrics) =>
        some(metrics, (metric) => (metric.value ?? 0) > 0),
      );
    return some(allChartProps, (chartProps) => dataPointsGreaterThan0(chartProps));
  }, [metricsResult.metrics, instanceId]);

  const onRefresh = useCallback(() => {
    refreshTimeRange();
    track(OPS_EVENTS.MIGRATION_READINESS_REPORT_REFRESH);
  }, [refreshTimeRange]);

  const onZoom = useCallback(
    (zoomedTimeRange: TimeRange | null = null) => {
      zoomIn(zoomedTimeRange);
      if (zoomedTimeRange) {
        track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CHART_ZOOM_IN, analyticsPropsFromTimeRange(zoomedTimeRange));
      }
    },
    [zoomIn],
  );

  const onZoomReset = useCallback(() => {
    // Instantly zooms out since timeRangeDerived metrics are cached
    zoomIn(null);
  }, [zoomIn]);

  const [selectedTimelineAccordionItems, setSelectedTimelineAccordionItems] = useState([1]);
  const [selectedAccordionItems, setSelectedAccordionItems] = useState([1, 2, 3]);

  const showTestMigrationModal = () => {
    track(OPS_EVENTS.MIGRATION_READINESS_REPORT_START_TEST_MIGRATION_DIALOG);
    testMigrationModal.setError(null);
    testMigrationModal.refreshKey();
    testMigrationModal.show();
  };
  const showLiveMigrationModal = () => {
    track(OPS_EVENTS.MIGRATION_READINESS_REPORT_START_LIVE_MIGRATION_DIALOG);
    liveMigrationModal.setError(null);
    liveMigrationModal.refreshKey();
    liveMigrationModal.show();
  };

  const datesTooFarApart = useMemo(
    () => !datesWithin24H(chartContext.timeRange.startTime, chartContext.timeRange.endTime),
    [chartContext.timeRange],
  );

  const { instances } = useInstances({ shouldPoll: true });

  const migrationTargetsInProgress = useMemo(() => {
    return instances.filter(
      (i) =>
        i.clonedFrom === instanceId &&
        i.instanceStatus !== INSTANCE_STATUS.RUNNING &&
        i.instanceStatus !== INSTANCE_STATUS.DESTROYING &&
        i.instanceStatus !== INSTANCE_STATUS.PAUSED &&
        i.instanceStatus !== INSTANCE_STATUS.PAUSING &&
        i.instanceStatus !== INSTANCE_STATUS.RESUMING &&
        i.instanceStatus !== INSTANCE_STATUS.UPDATING,
    );
  }, [instanceId, instances]);

  const migrationTargetsToFinalize = useMemo(
    () =>
      instances.filter(
        (i) =>
          i.clonedFrom === instanceId &&
          (i.instanceStatus === INSTANCE_STATUS.RUNNING ||
            i.instanceStatus === INSTANCE_STATUS.UPDATING ||
            i.instanceStatus === INSTANCE_STATUS.PAUSED ||
            i.instanceStatus === INSTANCE_STATUS.RESUMING ||
            i.instanceStatus === INSTANCE_STATUS.PAUSING),
      ),
    [instanceId, instances],
  );

  const liveMigrationInProgress = useMemo(() => {
    return (
      migrationTargetsInProgress.some((i) => kindOfMigration(i) === 'live') ||
      migrationTargetsToFinalize.some((i) => kindOfMigration(i) === 'live')
    );
  }, [migrationTargetsInProgress, migrationTargetsToFinalize]);

  if (instance && !needsMigration(instance.desiredSettings.version)) {
    return (
      <Banner className="m-4" usage="inline">
        Instance does not need migration
      </Banner>
    );
  }

  if (isLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  if (error) {
    return (
      <Banner className="m-4" usage="inline">
        An error occurred fetching the instance
      </Banner>
    );
  }

  function getTooltip(i: Instance) {
    if (i.tier === TIER.FREE) {
      return 'Automatic migration is not possible for free tier instances. We recommend: Download a snapshot of the instance, delete the instance, create a new instance and restore from downloaded backup file.';
    }
    if (i.instanceStatus !== INSTANCE_STATUS.RUNNING) {
      return 'The instance is currently not running. It has to be in RUNNING state to proceed with migration.';
    }
    return "You don't have permission to perform this action.";
  }

  function putMigrationButtons() {
    if (!instance) {
      return <></>;
    }
    const allCriteria =
      allowDelete && allowClone && instance.instanceStatus === INSTANCE_STATUS.RUNNING && instance.tier !== TIER.FREE;
    return (
      <>
        <Tooltip isDisabled={allCriteria} type="simple" isPortaled={false}>
          <Tooltip.Trigger hasButtonWrapper>
            <div className="display-inline mb-6 flex gap-4 pt-2">
              <Button
                isDisabled={!allCriteria}
                fill="outlined"
                onClick={showTestMigrationModal}
                htmlAttributes={{
                  'data-testid': 'test-migration',
                }}
              >
                Run migration test
              </Button>
              <Button
                isDisabled={!allCriteria || liveMigrationInProgress}
                fill="filled"
                onClick={showLiveMigrationModal}
                htmlAttributes={{
                  'data-testid': 'live-migration',
                }}
              >
                Live migration
              </Button>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Content style={{ width: 250, position: 'fixed' }}>{getTooltip(instance)}</Tooltip.Content>
        </Tooltip>
      </>
    );
  }

  return (
    <div>
      <div className="h-full overflow-auto px-4 pt-4">
        <BackButton to="../" />
        <div className="justify display-inline float-left mb-6 flex items-baseline gap-2 px-2 pt-2">
          <Typography variant="h2">Migration readiness report</Typography>
          <Typography variant="h3" className="font-normal">
            {instance?.name}
          </Typography>
          <TextLink type="external" href="https://neo4j.com/docs/aura/managing-instances/migration-readiness/">
            Docs
          </TextLink>
        </div>
        <div className="display-inline float-right mb-6 flex justify-end gap-4 px-2 pt-2">
          {putMigrationButtons()}
          <MigrationModal
            onClose={() => {
              track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CANCEL_TEST_MIGRATION_DIALOG);
              testMigrationModal.close();
            }}
            onDownload={() => {
              track(OPS_EVENTS.MIGRATION_READINESS_REPORT_DOWNLOAD_CREDENTIALS_TEST_MIGRATION);
              testMigrationModal.close();
            }}
            open={testMigrationModal.visible}
            loading={testMigrationModal.loading}
            error={testMigrationModal.error}
            key={testMigrationModal.key}
            liveMigration={false}
            name={instance!.name}
          />
          <MigrationModal
            onClose={() => {
              track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CANCEL_LIVE_MIGRATION_DIALOG);
              liveMigrationModal.close();
            }}
            onDownload={() => {
              track(OPS_EVENTS.MIGRATION_READINESS_REPORT_DOWNLOAD_CREDENTIALS_LIVE_MIGRATION);
              testMigrationModal.close();
            }}
            open={liveMigrationModal.visible}
            loading={liveMigrationModal.loading}
            error={liveMigrationModal.error}
            key={liveMigrationModal.key}
            liveMigration={true}
            name={instance!.name}
          />
        </div>
      </div>
      <div className="px-4">
        {migrationTargetsInProgress.map((migrationTarget) => {
          return <CloningInProgressSection key={migrationTarget.id} instance={migrationTarget} />;
        })}
        {isNotNullish(instance) &&
          migrationTargetsToFinalize.map((migrationTarget) => {
            return (
              <MigrationEvaluationSection
                key={migrationTarget.id}
                originalInstance={instance}
                migratedInstance={migrationTarget}
              />
            );
          })}
        <div className="bg-palette-neutral-bg-weak mb-8 w-full rounded-3xl">
          <Accordion
            className="relative p-1"
            expandedItemIds={selectedTimelineAccordionItems}
            isMultiple
            onChange={setSelectedTimelineAccordionItems}
          >
            <div className="z-10 flex justify-end">
              <div className="absolute top-0 m-1.5 flex flex-row flex-wrap items-center gap-2">
                <TimePeriodAndRefresh
                  timePeriod={chartContext.timePeriod}
                  timeRange={chartContext.timeRange}
                  warningText={
                    datesTooFarApart
                      ? "At most 24 hours' worth of logs can be fetched. Drag in the chart to narrow time window."
                      : undefined
                  }
                  onTimePeriodChange={setTimePeriodTracked}
                  onRefresh={onRefresh}
                />
              </div>
            </div>
            <Accordion.Item
              itemId={1 /* If 0 then it stops working */}
              className="accordion-item-no-padding borderless-accordion accordion-hover-rounded-3xl !border-b-0"
              title="Deprecations and query timeline"
            >
              <TimelineCard
                key="timeline-chart"
                context={chartContext}
                onZoom={onZoom}
                onZoomReset={onZoomReset}
                metricsResult={metricsResult}
                chartConfigs={allChartProps}
                mainChartConfigIdx={1}
                pills={chartPills}
                includeZeroValueSeries={[QueryExecutionsSuccessRate]}
                builderModifier={(builder) => {
                  builder.addExtraAxis({
                    label: QueryExecutionsSuccessRate.yAxisLabel,
                    stroke: isDarkTheme
                      ? tokens.colors.forest[DARK_MODE_SHADE]
                      : tokens.colors.forest[LIGHT_MODE_SHADE],
                  });

                  builder.series.forEach((series) => {
                    if (series.label === seriesNameMapper(QueryExecutionsSuccessRate)) {
                      series.scale = 'right';
                      series.points = { show: false };
                      series.fill =
                        typeof series.stroke === 'string' ? `${series.stroke}${chartPills[0]?.areaAlpha}` : series.fill;
                    }
                  });
                }}
                timeRangePadded
              />
            </Accordion.Item>
          </Accordion>
        </div>

        <div className="bg-palette-neutral-bg-weak w-full rounded-3xl">
          <Typography variant="body-medium" className="text-palette-neutral-text-weak flex flex-col gap-4 p-4" as="div">
            <p>
              This report page shows and advises you on anything that would stop or hinder from migrating to the latest
              Aura version. It provides information based on your current usage.
            </p>
            <p>
              The process to become &quot;Ready to migrate&quot; most likely includes manual changes such as replacing
              deprecated Cypher functionalities in your queries and updating older drivers and indexes. The following
              passages will help you with that in detail.
            </p>
          </Typography>

          <Accordion
            className="relative p-1"
            expandedItemIds={selectedAccordionItems}
            isMultiple
            onChange={setSelectedAccordionItems}
          >
            <Accordion.Item
              itemId={1}
              title="Cypher deprecations"
              className="accordion-item-no-padding borderless-accordion accordion-hover-rounded-3xl relative !border-b-0"
            >
              <DeprecationLogs
                chartTimeRange={chartContext.timeRange}
                hasDeprecations={hasQueries}
                selectedInstanceId={instanceId ?? ''}
              />
            </Accordion.Item>

            <Accordion.Item
              itemId={2}
              title="Deprecated driver usage"
              className="accordion-item-no-padding borderless-accordion accordion-hover-rounded-3xl relative !border-b-0"
            >
              <DriverLogs
                aqlaUrl={`/projects/${projectId}/logs?database=${instanceId}`}
                chartTimeRange={chartContext.timeRange}
                hasQueries={hasQueries}
                selectedInstanceId={instanceId ?? ''}
              />
            </Accordion.Item>

            <Accordion.Item
              itemId={3}
              title="Deprecated index types"
              className="accordion-item-no-padding borderless-accordion accordion-hover-rounded-3xl !border-b-0"
            >
              <DeprecatedIndexTypes instance={instance} />
            </Accordion.Item>
          </Accordion>
        </div>
        <div className="mt-4 px-2">
          <h5>Next steps</h5>
          {putMigrationButtons()}
        </div>
      </div>
    </div>
  );
};
